import React, { useState, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { colors } from '../../theme';
import { zIndex } from '../../constants/zIndex';

export interface ScrollableChipSelectorProps {
  chips: string[] | [string, string][];
  value: string;
  onChange: (value: string) => void;
  rowCount?: number;
}

// Constants for scroll behavior
const SCROLL_AMOUNT = 350;
const SCROLL_THRESHOLD = 10;

export const ScrollableChipSelector: React.FC<ScrollableChipSelectorProps> = ({
  chips,
  value,
  onChange,
  rowCount = 2,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Normalize chips to handle both string[] and [string, string][]
  const normalizedChips = chips.map(chip => {
    if (Array.isArray(chip)) {
      return { display: chip[0], tooltip: chip[1], value: chip[0] };
    }
    return { display: chip, tooltip: undefined, value: chip };
  });

  // Parse the current value into individual topics (case-insensitive matching)
  const getSelectedChips = (): Set<string> => {
    if (!value.trim()) return new Set();
    const items = value.split(',').map(item => item.trim().toLowerCase()).filter(Boolean);
    return new Set(items);
  };

  const selectedChips = getSelectedChips();

  const isChipSelected = (chipValue: string): boolean => {
    return selectedChips.has(chipValue.toLowerCase());
  };

  const handleChipClick = (chipValue: string) => {
    const currentItems = value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

    const chipLower = chipValue.toLowerCase();
    const existingIndex = currentItems.findIndex(
      item => item.toLowerCase() === chipLower
    );

    let newItems: string[];
    if (existingIndex >= 0) {
      // Remove the chip
      newItems = [...currentItems];
      newItems.splice(existingIndex, 1);
    } else {
      // Add the chip
      newItems = [...currentItems, chipValue];
    }

    onChange(newItems.join(', '));
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: direction === 'right' ? SCROLL_AMOUNT : -SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  // Split chips into rows (interlaced to distribute priority items evenly)
  type NormalizedChip = { display: string; tooltip: string | undefined; value: string };
  const rows: NormalizedChip[][] = Array.from({ length: rowCount }, () => []);
  normalizedChips.forEach((chip, index) => {
    rows[index % rowCount].push(chip);
  });

  return (
    <Box sx={{
      position: 'relative',
      marginBottom: '12px',
      marginLeft: showLeftArrow ? '-24px' : 0,
      marginRight: showRightArrow ? '-24px' : 0,
    }}>
      {/* Left arrow with gradient */}
      {showLeftArrow && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '48px',
            background: `linear-gradient(to right, ${colors.pureWhite} 0%, ${colors.pureWhite} 40%, transparent 100%)`,
            zIndex: zIndex.raised,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <IconButton
            onClick={() => scroll('left')}
            size="small"
            sx={{
              pointerEvents: 'auto',
              backgroundColor: colors.pureWhite,
              boxShadow: `0 2px 8px ${colors.blackAlpha[10]}`,
              '&:hover': {
                backgroundColor: colors.slate[100],
              },
            }}
          >
            <ChevronLeftIcon sx={{ color: colors.slate[600], fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      )}

      {/* Scrollable container */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minWidth: 'max-content',
          paddingLeft: showLeftArrow ? '24px' : '4px',
          paddingRight: showRightArrow ? '24px' : '4px',
        }}>
          {rows.map((row, rowIndex) => (
            <Box key={rowIndex} sx={{ display: 'flex', gap: '8px' }}>
              {row.map((chip) => {
                const selected = isChipSelected(chip.value);
                const chipButton = (
                  <Box
                    key={chip.value}
                    component="button"
                    type="button"
                    onClick={() => handleChipClick(chip.value)}
                    sx={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      border: `1px solid ${selected ? colors.signalOrange : colors.slate[200]}`,
                      backgroundColor: selected ? colors.signalOrangeLight : colors.softWhite,
                      color: selected ? colors.signalOrange : colors.slate[600],
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: selected ? colors.signalOrangeLight : colors.slate[100],
                        borderColor: selected ? colors.signalOrange : colors.slate[400],
                      },
                    }}
                  >
                    {chip.display}
                  </Box>
                );

                // Wrap with tooltip if tooltip text exists
                if (chip.tooltip) {
                  return (
                    <Tooltip key={chip.value} title={chip.tooltip} arrow>
                      {chipButton}
                    </Tooltip>
                  );
                }

                return chipButton;
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right arrow with gradient */}
      {showRightArrow && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '48px',
            background: `linear-gradient(to left, ${colors.pureWhite} 0%, ${colors.pureWhite} 40%, transparent 100%)`,
            zIndex: zIndex.raised,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pointerEvents: 'none',
          }}
        >
          <IconButton
            onClick={() => scroll('right')}
            size="small"
            sx={{
              pointerEvents: 'auto',
              backgroundColor: colors.pureWhite,
              boxShadow: `0 2px 8px ${colors.blackAlpha[10]}`,
              '&:hover': {
                backgroundColor: colors.slate[100],
              },
            }}
          >
            <ChevronRightIcon sx={{ color: colors.slate[600], fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
